import React, { useState } from 'react';

interface DomainConfig {
  customDomain: string;
  sslEnabled: boolean;
  sslProvider: 'letsencrypt' | 'custom' | 'cloudflare' | 'aws-acm';
  certificatePath?: string;
  privateKeyPath?: string;
  cloudflareApiToken?: string;
  awsAcmCertificateArn?: string;
  autoRenew: boolean;
}

export const DomainSettings: React.FC = () => {
  const [config, setConfig] = useState<DomainConfig>({
    customDomain: '',
    sslEnabled: false,
    sslProvider: 'letsencrypt',
    autoRenew: true,
  });

  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const currentDomain = 'guston-portal-dev-frontend-324037308280.s3-website-us-west-2.amazonaws.com';

  const handleVerifyDomain = async () => {
    setVerifying(true);
    setVerificationStatus(null);

    try {
      // TODO: API call to verify domain
      await new Promise(resolve => setTimeout(resolve, 2000));
      setVerificationStatus({
        success: true,
        message: 'Domain verified successfully! DNS records are configured correctly.',
      });
    } catch (error) {
      setVerificationStatus({
        success: false,
        message: 'Domain verification failed. Please check your DNS settings.',
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // TODO: API call to save settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Domain settings saved successfully!');
    } catch (error) {
      alert('Failed to save domain settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Domain & SSL Configuration</h2>
        <p className="text-gray-600">Configure custom domain and SSL certificate for your portal</p>
      </div>

      {/* Current Domain */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="text-blue-600 mr-3 text-xl">ℹ️</div>
          <div>
            <h3 className="text-sm font-semibold text-blue-900 mb-1">Current Domain</h3>
            <p className="text-sm text-blue-800 font-mono">{currentDomain}</p>
            <p className="text-xs text-blue-700 mt-2">
              This is your default S3 website domain. Configure a custom domain below for a professional URL.
            </p>
          </div>
        </div>
      </div>

      {/* Custom Domain Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Custom Domain</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Domain Name
          </label>
          <input
            type="text"
            value={config.customDomain}
            onChange={(e) => setConfig({ ...config, customDomain: e.target.value })}
            placeholder="portal.gustonltd.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter your custom domain (e.g., portal.gustonltd.com or app.yourcompany.com)
          </p>
        </div>

        {/* DNS Configuration Guide */}
        {config.customDomain && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-4">DNS Configuration Required</h4>
            <p className="text-sm text-gray-600 mb-4">
              Add the following DNS records to your domain registrar:
            </p>

            <div className="space-y-3">
              <div className="bg-white p-4 rounded border border-gray-300">
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Type:</span>
                    <span className="ml-2 font-mono">CNAME</span>
                  </div>
                  <div className="col-span-2">
                    <span className="font-medium text-gray-700">Name:</span>
                    <span className="ml-2 font-mono">{config.customDomain}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Value:</span>
                    <span className="ml-2 font-mono text-xs">{currentDomain}</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleVerifyDomain}
              disabled={verifying}
              className="mt-4 px-6 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50"
            >
              {verifying ? 'Verifying...' : 'Verify DNS Configuration'}
            </button>

            {verificationStatus && (
              <div className={`mt-4 p-4 rounded-lg ${
                verificationStatus.success
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${
                  verificationStatus.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {verificationStatus.message}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* SSL/TLS Configuration */}
      <div className="space-y-4 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">SSL/TLS Certificate</h3>
            <p className="text-sm text-gray-600">Secure your portal with HTTPS</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.sslEnabled}
              onChange={(e) => setConfig({ ...config, sslEnabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-accent-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-900">
              {config.sslEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>

        {config.sslEnabled && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                SSL Certificate Provider
              </label>
              <div className="grid grid-cols-2 gap-4">
                {([
                  { id: 'letsencrypt', name: "Let's Encrypt", desc: 'Free, automatic SSL' },
                  { id: 'aws-acm', name: 'AWS ACM', desc: 'AWS Certificate Manager' },
                  { id: 'cloudflare', name: 'Cloudflare', desc: 'Cloudflare SSL' },
                  { id: 'custom', name: 'Custom', desc: 'Upload your own certificate' },
                ] as const).map(provider => (
                  <button
                    key={provider.id}
                    onClick={() => setConfig({ ...config, sslProvider: provider.id })}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      config.sslProvider === provider.id
                        ? 'border-accent-500 bg-accent-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{provider.name}</div>
                    <div className="text-xs text-gray-600 mt-1">{provider.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Let's Encrypt Configuration */}
            {config.sslProvider === 'letsencrypt' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="text-green-600 mr-3">✓</div>
                  <div>
                    <h4 className="text-sm font-semibold text-green-900 mb-1">Automatic SSL with Let's Encrypt</h4>
                    <p className="text-sm text-green-800 mb-3">
                      SSL certificate will be automatically generated and renewed every 90 days.
                    </p>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={config.autoRenew}
                        onChange={(e) => setConfig({ ...config, autoRenew: e.target.checked })}
                        className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-green-800">
                        Enable automatic renewal
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AWS ACM Configuration */}
            {config.sslProvider === 'aws-acm' && (
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-orange-600 mr-3">ℹ️</div>
                    <div className="text-sm text-orange-800">
                      <p className="font-semibold mb-1">AWS Certificate Manager Setup:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Request a certificate in AWS ACM for your domain</li>
                        <li>Verify domain ownership via DNS or email</li>
                        <li>Copy the certificate ARN below</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certificate ARN
                  </label>
                  <input
                    type="text"
                    value={config.awsAcmCertificateArn || ''}
                    onChange={(e) => setConfig({ ...config, awsAcmCertificateArn: e.target.value })}
                    placeholder="arn:aws:acm:us-west-2:123456789012:certificate/12345678-1234-1234-1234-123456789012"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
              </div>
            )}

            {/* Cloudflare Configuration */}
            {config.sslProvider === 'cloudflare' && (
              <div className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="text-orange-600 mr-3">ℹ️</div>
                    <div className="text-sm text-orange-800">
                      <p className="font-semibold mb-1">Cloudflare SSL Setup:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Add your domain to Cloudflare</li>
                        <li>Update nameservers at your domain registrar</li>
                        <li>SSL will be automatically enabled</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cloudflare API Token (Optional)
                  </label>
                  <input
                    type="password"
                    value={config.cloudflareApiToken || ''}
                    onChange={(e) => setConfig({ ...config, cloudflareApiToken: e.target.value })}
                    placeholder="Enter your Cloudflare API token for automatic DNS management"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Custom Certificate */}
            {config.sslProvider === 'custom' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certificate File (.crt or .pem)
                  </label>
                  <input
                    type="file"
                    accept=".crt,.pem"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload your SSL certificate file</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Private Key File (.key)
                  </label>
                  <input
                    type="file"
                    accept=".key"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload your private key file</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certificate Chain (Optional)
                  </label>
                  <input
                    type="file"
                    accept=".crt,.pem"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload intermediate certificate chain if required</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CloudFront Distribution (Optional) */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          🚀 Optional: AWS CloudFront CDN
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          For better performance and SSL support on custom domains, consider setting up AWS CloudFront:
        </p>
        <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
          <li>Global content delivery network (CDN)</li>
          <li>HTTPS support with custom domains</li>
          <li>Better performance and caching</li>
          <li>DDoS protection and security</li>
        </ul>
        <button className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium">
          Setup CloudFront Distribution
        </button>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors shadow-sm disabled:opacity-50 font-medium"
        >
          {saving ? 'Saving...' : 'Save Domain Settings'}
        </button>
      </div>
    </div>
  );
};
